import {
  McPreDraftSurvey,
  McSpvEachSurveyRequest,
  McSpvSurveyRequest,
  MultiCountrySpvSurvey,
  MultiCountrySpvSurveyState,
} from '../interface/multi-country.interface';
import {
  BuyerSurveyResponse,
  MarginOverride,
  ScheduleLaunch,
  Qualification,
  Quotas,
  UniqueLinks,
} from '../../../../../shared/interfaces/survey.interface';
import { FormGroup } from '@angular/forms';
import { QuotaPayload } from '../../../../../shared/interfaces/quota.interface';
import { QualificationPayload } from '../../../../../shared/interfaces/qualification.interface';
import { Constants as CreateSurveyConstants } from '../../../../../shared/constants/create-survey.constants';
import { UserType } from '../../../../../shared/types/user-type';
import { BuyerConfig } from '../../../../../shared/interfaces/buyer-config.interface';
import { McSameSettingsDecisionMaker } from './mc-same-settings-decision-maker';
import { DEFAULT } from '../../../../../shared/constants/defaults';

export class MultiCountrySvpAddSurveyMapper {
  constructor(private _mcPreDraftSurveys: McPreDraftSurvey[]) {}

  public transform(): McSpvSurveyRequest {
    const {
      survey_title,
      survey_category_code,
      completes_required,
      field_time,
      expected_loi,
      expected_ir,
      qualifications = [],
      quotas = [],
      margin_override,
      offer_price,
      click_balancing,
    } = this._mcPreDraftSurveys[0];
    return {
      survey_title,
      survey_category_code,
      completes_required,
      field_time,
      expected_loi,
      expected_ir,
      margin_override,
      offer_price,
      qualifications,
      quotas,
      survey_localizations: this._localizations(),
      surveys: this._surveys(),
      click_balancing,
    };
  }

  private _surveys(): McSpvEachSurveyRequest[] {
    return [];
  }

  private _localizations(): string[] {
    return this._mcPreDraftSurveys?.map(
      (mcPreDraftSurvey) => mcPreDraftSurvey.survey_localization
    );
  }
}

export class MultiCountrySvpAddNewSurveyMapper {
  constructor(
    private readonly _mcSpvPayload: McSpvSurveyRequest,
    private readonly _upcomingLocalization: string,
    private readonly _isServiceBuyer: boolean = false
  ) {}

  public transform(): McSpvSurveyRequest {
    return {
      ...this._mcSpvPayload,
      survey_localizations: this._localizations(),
      surveys: this._surveys(),
    };
  }

  private _surveys(): McSpvEachSurveyRequest[] {
    return [
      ...this._mcSpvPayload.surveys,
      {
        survey_localization: this._upcomingLocalization,
        completes_required: this._mcSpvPayload.surveys[0].completes_required, // Picking value of first child,
        is_same_file_entry_links:
          !!this._mcSpvPayload.surveys[0].is_same_file_entry_links,
        ...((this._isSameOfferPrice() || this._isServiceBuyer) && {
          offer_price: this._mcSpvPayload.surveys[0].offer_price,
        }),
      },
    ];
  }

  private _isSameOfferPrice() {
    return this._mcSpvPayload.surveys.every(
      (survey) =>
        survey.offer_price === this._mcSpvPayload.surveys[0].offer_price
    );
  }

  private _localizations(): string[] {
    return [
      ...this._mcSpvPayload.survey_localizations,
      this._upcomingLocalization,
    ];
  }
}

export class MultiCountrySpvSurveyMapper {
  constructor(
    private readonly _surveys: BuyerSurveyResponse[],
    private readonly _mcUniqueId: string
  ) {}

  public transform(): MultiCountrySpvSurvey[] {
    return this._surveys.map((survey) => this._mapper(survey));
  }

  private _mapper(survey: BuyerSurveyResponse): MultiCountrySpvSurvey {
    const {
      buyer_company,
      buyer_user_id,
      ps_survey_id: survey_id,
      ps_survey_status,
      survey_title,
      survey_category_code,
      locale: {
        country_code,
        country_name,
        language_translate: language_code,
        language_code: language_short_code,
        language_name,
      },
      survey_localization,
      completes_required,
      expected_ir,
      expected_loi,
      field_time,
      click_balancing,
      currency_symbol,
    } = survey;
    const aida = DEFAULT.AIDA_PAYLOAD;
    return {
      buyer_company,
      buyer_user_id,
      survey_id,
      ps_survey_status,
      survey_title,
      survey_category_code,
      country_code,
      country_name,
      language_code,
      language_short_code,
      language_name,
      survey_localization,
      click_balancing,
      completes_required,
      expected_ir,
      expected_loi,
      field_time,
      mc_unique_id: this._mcUniqueId,
      feasibility: 0,
      cpi: this._cpi(survey),
      price_override: this._priceOverride(survey),
      currency_symbol,
      loading: false,
      state: this._state(),
      estimated_clicks: survey.estimated_clicks,
      aida,
    };
  }

  private _cpi(survey: BuyerSurveyResponse): number {
    return survey.acpi ?? (survey.cpi.current || 0);
  }

  private _priceOverride(survey: BuyerSurveyResponse): number {
    return Number(survey.offer_price);
  }

  private _state(): MultiCountrySpvSurveyState {
    return {
      cpi: 'view',
      completes_required: 'view',
    };
  }
}

export class MultiCountrySpvSurveyPayloadMapper {
  constructor(
    private readonly _responseSurveys: BuyerSurveyResponse[],
    private readonly _userType: UserType,
    private readonly _isServiceUser: boolean = false
  ) {}

  public transform(): McSpvSurveyRequest {
    const {
      survey_title,
      survey_category_code,
      completes_required,
      click_balancing,
      estimated_clicks,
      field_time,
      expected_loi,
      expected_ir,
      live_url = '',
      test_url = '',
      unique_links,
      dq_settings,
      is_dq_enable,
      basic_token,
      schedule_launch,
      soft_launch,
      quota_throttling,
      is_feot_enabled,
      feot,
      incl_excl,
      survey_grouping = [],
      survey_external_id = '',
      subscriber_emails = [],
      pm_emails,
      buyer_message = '',
      bpm,
      blend_id,
      supplier_allocation_throttle,
    } = this._parent();

    return {
      survey_title,
      survey_category_code,
      completes_required,
      click_balancing,
      estimated_clicks,
      field_time,
      expected_loi,
      expected_ir,
      ...this._handleEmpty('live_url', this._manageLiveUrl(live_url)),
      ...this._handleEmpty('test_url', this._manageTestUrl(test_url)),
      ...this._handleEmpty('billing_id', this._billingId()),
      unique_links: this._getUniqueLinks(unique_links), // If first child survey has unique links enabled, it assign to parent survey.
      dq_settings,
      is_dq_enable,
      basic_token,
      schedule_launch: this._scheduleLaunch(schedule_launch),
      soft_launch,
      quota_throttling,
      is_feot_enabled,
      feot,
      incl_excl,
      survey_grouping,
      survey_external_id,
      subscriber_emails,
      pm_emails,
      blend_id,
      ...this._getOfferPriceAndMarginDetails(this._parent(), false),
      ...this._handleEmpty('buyer_message', buyer_message),
      bpm,
      ...this._getOperatorSpecificFields(this._parent()),
      survey_localizations: this._localizations(),
      surveys: this._surveys(),
      // Keeping qualifications and quotas of parent empty as of now
      qualifications: [],
      quotas: [],
      supplier_allocation_throttle,
    };
  }

  private _billingId(): string {
    return this._parent().billing_id;
  }

  private _getOfferPriceAndMarginDetails(
    survey: BuyerSurveyResponse,
    isChild: boolean = true
  ): Partial<{
    offer_price: number;
    margin_override: Pick<MarginOverride, 'margin'>;
  }> {
    const {
      margin_override: marginOverride,
      is_enable_margin_override = false,
      offer_price = 0,
    } = survey;

    if (this._isServiceUser && is_enable_margin_override) {
      return {
        offer_price: offer_price ? offer_price : 0,
        margin_override: marginOverride!,
      };
    }

    if (offer_price && isChild) {
      return {
        offer_price,
      };
    }

    return {};
  }

  private _parent(): BuyerSurveyResponse {
    return this._responseSurveys.find(
      (survey) => survey.mc && !!survey.mc.SVP && !!survey.mc.parent
    )!;
  }

  private _childs(): BuyerSurveyResponse[] {
    return this._responseSurveys.filter(
      (survey) => survey.mc && !!survey.mc.SVP && !survey.mc.parent
    )!;
  }

  private _localizations(): string[] {
    return this._childs().map((childSurvey) => childSurvey.survey_localization);
  }

  private _scheduleLaunch(scheduleLaunch: ScheduleLaunch): ScheduleLaunch {
    if (!scheduleLaunch.enable) {
      return {
        enable: false,
      };
    }

    return scheduleLaunch;
  }

  private _handleEmpty(
    key: keyof BuyerSurveyResponse,
    value: string
  ): Partial<BuyerSurveyResponse> {
    if (!value) {
      return {};
    }

    return {
      [key]: value,
    };
  }

  private _getOperatorSpecificFields(survey: BuyerSurveyResponse) {
    if (this._userType !== 'operator') {
      return {};
    }

    const { pm, crm_id, asana_id } = survey;
    return {
      pm,
      crmId: crm_id,
      asanaId: asana_id,
    };
  }

  private _surveys(): McSpvEachSurveyRequest[] {
    return this._childs().map((childSurvey) => {
      const {
        survey_localization,
        ps_survey_id: survey_id,
        completes_required,
        click_balancing,
        expected_loi,
        expected_ir,
        field_time,
        offer_price,
        live_url = '',
        test_url = '',
        unique_links,
        dq_settings,
        is_dq_enable,
        basic_token,
        schedule_launch,
        soft_launch,
        quota_throttling,
        is_feot_enabled,
        feot,
        incl_excl,
        survey_grouping = [],
        survey_external_id = '',
        subscriber_emails = [],
        pm_emails,
        buyer_message = '',
        bpm,
        qualifications = [],
        quotas = [],
        estimated_clicks,
        feasibility_cpi,
        blend_id,
        zipcode_file_path: zipcodeFilePath = '',
        fsacode_file_path: fsacodeFilePath = '',
        pricing_multiplier,
        supplier_allocation_throttle,
        is_same_file_entry_links,
      } = childSurvey;
      return {
        survey_localization,
        completes_required,
        estimated_clicks,
        click_balancing,
        expected_loi,
        expected_ir,
        field_time,
        ...(offer_price && { offer_price }),
        ...this._handleEmpty('live_url', live_url),
        ...this._handleEmpty('test_url', test_url),
        ...this._handleEmpty('billing_id', this._billingId()),
        unique_links,
        dq_settings,
        is_dq_enable,
        basic_token,
        schedule_launch: this._scheduleLaunch(schedule_launch),
        soft_launch,
        quota_throttling,
        is_feot_enabled,
        feot,
        incl_excl,
        survey_grouping,
        survey_external_id,
        subscriber_emails,
        pm_emails,
        ...this._handleEmpty('buyer_message', buyer_message),
        bpm,
        ...this._getOperatorSpecificFields(childSurvey),
        qualifications,
        survey_id,
        quotas,
        blend_id,
        ...this._getOfferPriceAndMarginDetails(childSurvey),
        feasibility_cpi,
        ...(zipcodeFilePath && { zipcodeFilePath }),
        ...(fsacodeFilePath && { fsacodeFilePath }),
        pricing_multiplier,
        supplier_allocation_throttle,
        is_same_file_entry_links:
          is_same_file_entry_links ||
          new McSameSettingsDecisionMaker(this._childs()).decide(
            'unique_links'
          ),
      };
    });
  }

  // Check the first child survey to determine if unique links are enabled and return unique links
  private _getUniqueLinks(unique_links: UniqueLinks) {
    const childSurvey = this._childs()[0];
    return childSurvey?.unique_links.enable
      ? childSurvey.unique_links
      : unique_links;
  }

  private _getUrl(url: string, type: 'live' | 'test'): string {
    if (url) {
      return url;
    }
    const childSurvey = this._childs()[0];
    if (!childSurvey.unique_links.enable) {
      return childSurvey?.[`${type}_url`] || '';
    }
    return '';
  }

  private _manageLiveUrl(live_url: string) {
    return this._getUrl(live_url, 'live');
  }

  private _manageTestUrl(test_url: string) {
    return this._getUrl(test_url, 'test');
  }
}

export class MultiCountrySpvCreateFormAllDataMapper {
  constructor(private readonly _form: FormGroup) {}

  public transform(): Omit<
    Partial<BuyerSurveyResponse>,
    'survey_localization'
  > {
    const {
      surveyTitle: survey_title,
      selectedCategory: {
        id: survey_category_code = CreateSurveyConstants.SURVEY_CATEGORY_CODE
          .EXCITING_NEW,
      } = {},
      numberOfCompletes: completes_required,
      lengthOfSurvey: expected_loi,
      incidence: expected_ir,
      fieldTime: field_time,
      clickBalancing: click_balancing,
    } = this._form.value;
    return {
      ...(this._isDirty('surveyTitle') && { survey_title }),
      ...(this._isDirty('numberOfCompletes') && { completes_required }),
      ...(this._isDirty('selectedCategory') && { survey_category_code }),
      ...(this._isDirty('lengthOfSurvey') && { expected_loi }),
      ...(this._isDirty('incidence') && { expected_ir }),
      ...(this._isDirty('fieldTime') && { field_time }),
      ...(this._isDirty('clickBalancing') && {
        click_balancing: click_balancing ? 1 : 0,
      }),
      ...((this._isDirty('clickBalancing') ||
        this._isDirty('numberOfCompletes')) &&
        click_balancing && {
          estimated_clicks: Math.round(completes_required * 100) / expected_ir,
        }),
    };
  }

  private _isDirty(
    key:
      | 'surveyTitle'
      | 'selectedCategory'
      | 'numberOfCompletes'
      | 'lengthOfSurvey'
      | 'incidence'
      | 'fieldTime'
      | 'buyerMargin'
      | 'buyerCpiOverride'
      | 'clickBalancing'
  ) {
    return this._form.controls[key].dirty;
  }
}

export class MultiCountrySpvCreateFormEachDataMapper {
  constructor(
    private readonly _localization: string,
    private readonly _qualifications: QualificationPayload[] = [],
    private readonly _quotas: QuotaPayload[] = [],
    private readonly _serviceBuyer: boolean = false,
    private readonly _offer_price: number,
    private readonly _buyerMargin: number = 0,
    private readonly _buyerConfig = <BuyerConfig>{}
  ) {}

  public transform(): Partial<BuyerSurveyResponse> {
    return {
      survey_localization: this._localization,
      qualifications: this._qualifications as Qualification[],
      quotas: this._quotas as Quotas[],
      ...(this._serviceBuyer &&
        this._offer_price && { offer_price: this._offer_price }),
      ...(this._serviceBuyer &&
        this._buyerMargin && {
          margin_override: { margin: this._buyerMargin },
        }),
      ...(this._serviceBuyer &&
        (this._buyerMargin || this._offer_price) && {
          is_enable_margin_override: this._hasMarginOverrideEnabled(
            this._buyerMargin
          ),
        }),
    };
  }

  private _hasMarginOverrideEnabled(margin: number): boolean {
    return (
      (this._buyerConfig.enableTargetMargin ||
        this._buyerConfig.enableTargetMarginLaunchAll) &&
      !isNaN(margin)
    );
  }
}

export class MultiCountrySpvFeasibilitySurveyRequestMapper {
  constructor(private readonly _surveys: McSpvEachSurveyRequest[]) {}

  public transform(): McSpvEachSurveyRequest[] {
    return this._surveys.map((survey) => this._mapper(survey));
  }

  private _mapper(survey: McSpvEachSurveyRequest): McSpvEachSurveyRequest {
    const {
      survey_localization,
      survey_id,
      completes_required,
      expected_loi,
      expected_ir,
      field_time,
      qualifications = [],
      quotas = [],
      offer_price,
      click_balancing,
    } = survey;

    return {
      survey_localization,
      survey_id,
      completes_required,
      expected_loi,
      expected_ir,
      field_time,
      qualifications,
      quotas,
      offer_price,
      estimated_clicks: Math.round(
        (completes_required! * 100) / expected_ir! || 0
      ),
      click_balancing,
    };
  }
}
